import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { atomFamily } from 'jotai-family';

import { getDraftSavedAtKey, recordDraftSaved } from '@states/drafts/draftSavedAt';

import type { ContractWarningTypeEnum } from '@lib/enums';
import type { AdditionalActionsEnum } from '@lib/enums/AdditionalActionsEnum';
import type { RewriteReasonEnum } from '@lib/enums/RewriteReasonEnum';
import type {
  TPlanOption,
  TPlanSurcharge,
  TQuoteUsageAndModifications,
  TTotalLossReport,
} from '@models';
import type {
  TAdditionalActionModel,
  TAdjustmentCostAndPayment,
  TContractFieldAdjustments,
} from '@models/retention';
import type { TAdjustmentVinAndMileage } from '@models/retention/TAdjustment';

export type TAdjustmentAtom = Readonly<{
  contractId?: number;
  isInitialized?: boolean;
  contractHasExactMileage?: Maybe<boolean>;
  vinAndMileage?: TAdjustmentVinAndMileage;
  costAndPayment?: TAdjustmentCostAndPayment;
  usageAndModifications?: MaybeAll<TQuoteUsageAndModifications>;
  additionalSurcharges?: TPlanSurcharge[];
  additionalOptions?: TPlanOption[];
  additionalActionSelections?: AdditionalActionsEnum;
  additionalActions?: TAdditionalActionModel;
  dueDateButton?: boolean;
  dueDateFinco?: boolean;
  deferDateButton?: boolean;
  deferFinco?: boolean;
  contractFieldAdjustments?: Readonly<TContractFieldAdjustments[]>;
  totalLossReport?: Maybe<TTotalLossReport>;
  requiresRewrite?: Maybe<boolean>;
  rewriteReason?: Maybe<RewriteReasonEnum>;
  requiresCancelFlat?: Maybe<boolean>;
  adjustmentDiff?: TContractFieldAdjustments[];
  remainingBalance?: number;
  numberOfPaymentsRemaining?: number;
  contractWarnings?: ContractWarningTypeEnum[];
}>;

export const INITIAL_ADJUSTMENT_STATE = {
  contractId: undefined,
  isInitialized: undefined,
  contractHasExactMileage: undefined,
  vinAndMileage: undefined,
  costAndPayment: undefined,
  usageAndModifications: undefined,
  additionalSurcharges: undefined,
  additionalOptions: undefined,
  additionalActionSelections: undefined,
  additionalActions: undefined,
  dueDateButton: undefined,
  dueDateFinco: undefined,
  deferDateButton: undefined,
  deferFinco: undefined,
  contractFieldAdjustments: undefined,
  totalLossReport: undefined,
  requiresRewrite: undefined,
  rewriteReason: undefined,
  requiresCancelFlat: undefined,
  adjustmentDiff: undefined,
  remainingBalance: undefined,
  numberOfPaymentsRemaining: undefined,
  contractWarnings: undefined,
};

export const ADJUSTMENT_ATOM_KEY_PREFIX = 'adjustmentAtom_';

export const getAdjustmentAtomKey = (contractId: number): string =>
  `${ADJUSTMENT_ATOM_KEY_PREFIX}${contractId}`;

export const adjustmentAtomFamily = atomFamily((contractId: number) => {
  const storageAtom = atomWithStorage<TAdjustmentAtom>(getAdjustmentAtomKey(contractId), {
    ...INITIAL_ADJUSTMENT_STATE,
    contractId,
  });

  return atom(
    (get) => get(storageAtom),
    (get, set, update: Partial<TAdjustmentAtom> | ((prev: TAdjustmentAtom) => TAdjustmentAtom)) => {
      const current = get(storageAtom);
      const isContractMismatch = current.contractId !== contractId;

      const newValue = isContractMismatch
        ? { ...INITIAL_ADJUSTMENT_STATE, contractId }
        : typeof update === 'function'
          ? update(current)
          : { ...current, ...update };

      set(storageAtom, newValue);
      recordDraftSaved(getAdjustmentAtomKey(contractId));
    },
  );
});

export const removeAdjustmentAtom = (contractId: number): void => {
  adjustmentAtomFamily.remove(contractId);
  const storageKey = getAdjustmentAtomKey(contractId);
  if (typeof window !== 'undefined') {
    localStorage.removeItem(storageKey);
    localStorage.removeItem(getDraftSavedAtKey(storageKey));
  }
};
